import { Add, ContentCopy, ContentPaste, Edit } from "@mui/icons-material";
import { Button, IconButton, InputAdornment, MenuItem, Switch, TextField, Tooltip } from "@mui/material";
import { useFormik } from "formik";
import moment from "moment";
import { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { API_URLS } from "../../config/APIUrls";
import axiosInstance from "../../config/axios";
import CustomDialog from "../../Shared/CustomDialogBox";
import CustomFilter from "../../Shared/CustomForFiler";
import CustomTable from "../../Shared/CustomTable";
import CustomToPagination from "../../Shared/CustomToPagination";

const VillageMaster = () => {
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [page, setPage] = useState(1);

    const filterFormik = useFormik({
        initialValues: { search: '', start_date: '', end_date: '', district: '', zone: '' },
        enableReinitialize: true
    });

    const { data: villageData, isLoading: loading } = useQuery(
        ["get_village_master", page, filterFormik.values.search, filterFormik.values.start_date, filterFormik.values.end_date, filterFormik.values.district, filterFormik.values.zone],
        () => axiosInstance.get(API_URLS.get_village_master, {
            params: {
                search: filterFormik.values.search,
                start_date: filterFormik.values.start_date,
                end_date: filterFormik.values.end_date,
                page: page,
                count: 10,
                district: filterFormik.values.district,
                zone: filterFormik.values.zone,
            }
        }),
        { refetchOnMount: false, refetchOnReconnect: false, refetchOnWindowFocus: false }
    );

    const villageList = villageData?.data?.response || [];

    const { data: districtData } = useQuery(
        "get_district_master",
        () => axiosInstance.get(API_URLS.get_district_master, { params: { count: 1000000, status: "1" } }),
        { refetchOnMount: false, refetchOnReconnect: false, refetchOnWindowFocus: false }
    );
    const districtOptions = districtData?.data?.response?.data || [];

    const fk = useFormik({
        initialValues: {
            name: "",
            address: "N/A",
            district: "",
            zone: "",
            lat: "",
            lng: "",
            radius: 1,
            status: "1"
        },
        onSubmit: async (values) => {
            if (editData) return await updateVillage(values);
            else return await createVillage(values);
        }
    });

    const createVillage = async (values) => {
        try {
            const reqBody = {
                m06_vi_name: values.name,
                m06_vi_address: values.address,
                m06_vi_district: values.district,
                m06_vi_zone: values.zone,
                m06_vi_lat: values.lat,
                m06_vi_lng: values.lng,
                m06_vi_radius: values.radius,
                m06_vi_status: "1"
            };
            const res = await axiosInstance.post(API_URLS.create_village_master, reqBody);
            toast(res?.data?.msg);
            if (res?.data?.success) {
                queryClient.invalidateQueries("get_village_master");
                handleClose();
            }
        } catch (e) {
            toast.error("Failed to create village");
        }
    };

    const updateVillage = async (values) => {
        try {
            const reqBody = {
                m06_vi_id: editData.m06_vi_id,
                m06_vi_name: values.name,
                m06_vi_address: values.address,
                m06_vi_district: values.district,
                m06_vi_zone: values.zone,
                m06_vi_lat: values.lat,
                m06_vi_lng: values.lng,
                m06_vi_radius: values.radius,
                m06_vi_status: values.status
            };
            const res = await axiosInstance.post(API_URLS.update_village_master, reqBody);
            toast(res?.data?.msg);
            if (res?.data?.success) {
                queryClient.invalidateQueries("get_village_master");
                handleClose();
            }
        } catch (e) {
            toast.error("Failed to update village");
        }
    };

    const toggleStatus = async (row, checked) => {
        try {
            const reqBody = { m06_vi_id: row.m06_vi_id, m06_vi_status: checked ? "1" : "0" };
            const res = await axiosInstance.post(API_URLS.village_master_status, reqBody);
            toast(res?.data?.msg);
            if (res?.data?.success) queryClient.invalidateQueries("get_village_master");
        } catch (e) {
            toast.error("Failed to update status");
        }
    };

    const handleExport = async () => {
        try {
            const res = await axiosInstance.get(API_URLS.get_village_excel, {
                search: filterFormik.values.search || "",
                start_date: filterFormik.values.start_date || "",
                end_date: filterFormik.values.end_date || "",
                district: filterFormik.values.district || "",
                zone: filterFormik.values.zone || ""
            })
            const fileUrl = res?.data?.response?.url;
            window.open(fileUrl, "_blank")
        }
        catch (e) {
            console.log("Something went wrong")
        }
    }
    const { data: zoneData } = useQuery(
        ["get_zone_master", filterFormik.values.district, fk.values.district],
        () => axiosInstance.get(API_URLS.get_zone_master, {
            params: {
                count: 1000000,
                status: "1",
                district:
                    fk.values.district !== ""
                        ? fk.values.district
                        : (filterFormik.values.district === "Select" ? "" : filterFormik.values.district)
            }
        }),
        { refetchOnMount: false, refetchOnReconnect: false, refetchOnWindowFocus: false }
    );
    const zoneOptions = zoneData?.data?.response?.data || [];

    const handleOpen = () => { fk.resetForm(); setEditData(null); setOpen(true); };
    const handleEdit = (row) => {
        setEditData(row);
        fk.setValues({
            name: row.m06_vi_name,
            address: row.m06_vi_address,
            district: String(row.m06_vi_district),
            zone: String(row.m06_vi_zone),
            lat: row.m06_vi_lat,
            lng: row.m06_vi_lng,
            radius: row.m06_vi_radius,
            status: String(row.m06_vi_status)
        });
        setOpen(true);
    };
    const handleClose = () => { setOpen(false); setEditData(null); };

    const tableHead = ["S.No", "Village Name", "Address", "District", "Zone", "Lat", "Lng", "Radius", "Status", "Actions", "Date"];
    const tableRows = villageList?.data?.map((item, index) => [
        <span>{index + 1 || "--"}</span>,
        <span>{item.m06_vi_name || "--"}</span>,
        <span>{item.m06_vi_address || "--"}</span>,
        <span>{item.m04_dist_name || "--"}</span>,
        <span>{item.m05_zn_name || "--"}</span>,
        <span>{item.m06_vi_lat || "--"}</span>,
        <span>{item.m06_vi_lng || "--"}</span>,
        <span>{Number(item.m06_vi_radius)?.toFixed(2) || "--"}</span>,
        <Switch checked={item.m06_vi_status === 1} onChange={(e) => toggleStatus(item, e.target.checked)} color="success" />,
        <div className="flex justify-center gap-3">
            <Tooltip title="Edit">
                <IconButton onClick={() => handleEdit(item)}>
                    <Edit className="text-blue-600" />
                </IconButton>
            </Tooltip>
        </div>,
        <span>{moment(item?.m06_vi_created_at).format("DD-MM-YYYY")}</span>
    ]);

    return (
        <div className="mx-5">
            <div className="data_filter_box">
                <div className="flex justify-between items-center gap-3 mb-5">
                   <p className="font-bold text-lg lg:text-xl whitespace-nowrap">Village Master</p>
                     <Button variant="contained" className="!bg-[#00008b]"  startIcon={<Add />} onClick={handleOpen}>
                        Add 
                    </Button>
                </div>
                <div className="flex lg:flex-row flex-col gap-4">
                    <div className="flex justify-start items-center gap-5">
                        <p> Select  </p>
                        <TextField
                        fullWidth
                            label="All District"
                            select
                            size="small"
                            id="district"
                            name="district"
                            value={filterFormik.values.district || ""}
                            onChange={(e) => {
                                filterFormik.handleChange(e);
                                filterFormik.setFieldValue("zone", "Select"); // reset zone
                            }}
                            className="!min-w-[150px]"
                        >
                            <MenuItem value={""}>ALL District</MenuItem>
                            {districtOptions.map(d => (
                                <MenuItem key={d.m04_dist_id} value={d.m04_dist_id}>
                                    {d.m04_dist_name}
                                </MenuItem>
                            ))}
                        </TextField>

                    </div>
                    <div className="flex justify-start items-center gap-5">
                        <p> Select  </p>
                        <TextField
                        fullWidth
                            label="All Block"
                            select
                            size="small"
                            id="zone"
                            name="zone"
                            value={filterFormik.values.zone || ""}
                            onChange={filterFormik.handleChange}
                            className="!min-w-[150px]"
                        >
                            <MenuItem value={""}>ALL Zone</MenuItem>
                            {zoneOptions.map(z => (
                                <MenuItem key={z.m05_zn_id} value={z.m05_zn_id}>
                                    {z.m05_zn_name}
                                </MenuItem>
                            ))}
                        </TextField>

                    </div>
                </div>
            </div>

            <CustomFilter
                formik={filterFormik}
                onFilter={() => { setPage(1); queryClient.invalidateQueries("get_village_master"); }}
                onClear={() => { filterFormik.resetForm(); setPage(1); queryClient.invalidateQueries("get_village_master"); }}
                onExport={handleExport}
            />

            <CustomTable tablehead={tableHead} tablerow={tableRows} isLoading={loading} />

            <CustomToPagination setPage={setPage} page={page} data={villageList} />

            <CustomDialog
                open={open}
                onClose={handleClose}
                onSubmit={fk.handleSubmit}
                title={editData ? "Edit Village" : "Add Village"}
                formik={fk}
                fields={[
                    { name: "name", label: "Village Name" },
                    {
                        name: "district",
                        label: "District",
                        type: "select",
                        onChange: (e) => {
                            fk.handleChange(e);
                            fk.setFieldValue("zone", "");
                        },
                        options: districtOptions.map(d => ({
                            label: d.m04_dist_name,
                            value: String(d.m04_dist_id)
                        }))
                    },
                    {
                        name: "zone",
                        label: "Zone",
                        type: "select",
                        options: zoneOptions.map(z => ({
                            label: z.m05_zn_name,
                            value: String(z.m05_zn_id)
                        }))
                    },
                    { name: "address", label: "Address" },
                    {
                        name: "lat",
                        label: "Latitude",
                        type: "number",
                        inputProps: { inputMode: "decimal" },
                        InputProps: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={async () => {
                                            try {
                                                const text = await navigator.clipboard.readText(); // read clipboard
                                                const numberValue = parseFloat(text);
                                                if (!isNaN(numberValue)) fk.setFieldValue("lat", numberValue);
                                                else toast.error("Clipboard does not contain a valid number!");
                                            } catch (err) {
                                                toast.error("Failed to read clipboard");
                                            }
                                        }}
                                    >
                                        <ContentPaste fontSize="small" /> {/* Paste icon */}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }
                    },
                    {
                        name: "lng",
                        label: "Longitude",
                        type: "number",
                        inputProps: { inputMode: "decimal" },
                        InputProps: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={async () => {
                                            try {
                                                const text = await navigator.clipboard.readText(); // read clipboard
                                                const numberValue = parseFloat(text);
                                                if (!isNaN(numberValue)) fk.setFieldValue("lng", numberValue);
                                                else toast.error("Clipboard does not contain a valid number!");
                                            } catch (err) {
                                                toast.error("Failed to read clipboard");
                                            }
                                        }}
                                    >
                                        <ContentPaste fontSize="small" /> {/* Paste icon */}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }
                    },

                    { name: "radius", label: "Radius" }
                ]}
            />

        </div>
    );
};

export default VillageMaster;

